import type { Booking, Resource } from '@/types';
import { UNALLOCATED_RESOURCE_ID } from '@/utils/unallocated-booking';

export type BookingResourceMoveFields = Pick<
  Booking,
  'resourceId' | 'resourceIds' | 'requiredRole' | 'requiredSkills' | 'resourceAssignments'
>;

/**
 * The resource-bound fields a booking takes on when it moves to another
 * resource: the destination resource and its role, the booking's own skills
 * left untouched, and any per-resource assignment re-keyed to the destination
 * so the persisted row keeps the role and skills the booking was set up with.
 *
 * A destination with no role of its own leaves the booking's role in place, and
 * a move back to Unallocated clears both role and skills.
 */
export function buildBookingResourceMoveFields({
  booking,
  targetResource,
  isLive,
  originalResourceId,
}: {
  booking: Pick<
    Booking,
    'resourceId' | 'resourceIds' | 'requiredRole' | 'requiredSkills' | 'resourceAssignments'
  >;
  targetResource: Pick<Resource, 'id' | 'role'>;
  isLive: boolean;
  /** The resource the booking sits on before the move; its primary id by default. */
  originalResourceId?: string | number | null;
}): BookingResourceMoveFields {
  const targetId = String(targetResource.id);

  if (targetId === UNALLOCATED_RESOURCE_ID) {
    return {
      resourceId: isLive ? null : UNALLOCATED_RESOURCE_ID,
      resourceIds: isLive ? [] : [UNALLOCATED_RESOURCE_ID],
      requiredRole: '',
      requiredSkills: [],
      resourceAssignments: undefined,
    };
  }

  const fromId = String(originalResourceId ?? booking.resourceId ?? '');

  // Staying on the same resource is not a move, so every resource field is left
  // exactly as it was. A multi-resource booking has one tile per resource: a
  // tile dragged or resized within its own row must keep the booking's other
  // resources and its primary resource rather than collapsing onto that tile's.
  if (fromId !== '' && fromId === targetId) {
    return {
      resourceId: booking.resourceId ?? targetResource.id,
      resourceIds: booking.resourceIds && booking.resourceIds.length > 0
        ? booking.resourceIds.map(String)
        : [targetId],
      requiredRole: booking.requiredRole ?? '',
      requiredSkills: booking.requiredSkills ?? [],
      resourceAssignments: booking.resourceAssignments,
    };
  }

  const resourceIds = booking.resourceIds && booking.resourceIds.length > 0
    ? booking.resourceIds.map((rid) => (String(rid) === fromId ? targetId : String(rid)))
    : [targetId];

  const previousAssignment = fromId ? booking.resourceAssignments?.[fromId] : undefined;
  const requiredSkills = booking.requiredSkills ?? previousAssignment?.requiredSkills ?? [];
  const requiredRole = targetResource.role?.trim()
    ? targetResource.role
    : booking.requiredRole ?? previousAssignment?.requiredRole ?? '';

  const resourceAssignments = booking.resourceAssignments
    ? Object.fromEntries(
      Object.entries(booking.resourceAssignments).map(([resourceId, assignment]) => (
        resourceId === fromId
          ? [targetId, { requiredRole, requiredSkills }]
          : [resourceId, assignment]
      )),
    )
    : { [targetId]: { requiredRole, requiredSkills } };

  return {
    resourceId: targetResource.id,
    resourceIds: Array.from(new Set(resourceIds)),
    requiredRole,
    requiredSkills,
    resourceAssignments,
  };
}
